// covered-by: frontends/carrie/src/routes/settings/routes/__tests__/warholOutputSettingsRoute.test.tsx
import { Flex, Heading, Spinner, Text, TextArea } from "@radix-ui/themes";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  emptyWarholOutputForm,
  formStateFromApi,
  type WarholOutputFormState,
  WARHOL_OUTPUT_FIELDS,
} from "./warholOutputSettingsFields";

import { useUI } from "../../../contexts/ui";
import {
  useUpdateWarholOutputSettingsMutation,
  useWarholOutputSettingsQuery,
} from "../../../utils/queries/warholOutputSettings";

const DEBOUNCE_MS = 1000;
const SAVED_ACK_MS = 2000;

function sameWarholOutputForm(
  left: WarholOutputFormState,
  right: WarholOutputFormState,
): boolean {
  return (
    left.claimsAdvisorInstructions === right.claimsAdvisorInstructions &&
    left.reportShape === right.reportShape &&
    left.replyFormat === right.replyFormat
  );
}

function useWarholOutputAutosave(data: WarholOutputFormState | undefined) {
  const { notify } = useUI();
  const updateMutation = useUpdateWarholOutputSettingsMutation();
  const [form, setForm] = useState<WarholOutputFormState>(
    emptyWarholOutputForm(),
  );
  const [isSaved, setIsSaved] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormRef = useRef<WarholOutputFormState>(form);
  const editGenerationRef = useRef(0);
  const appliedGenerationRef = useRef(0);
  const mutateAsyncRef = useRef(updateMutation.mutateAsync);
  const notifyRef = useRef(notify);
  latestFormRef.current = form;
  mutateAsyncRef.current = updateMutation.mutateAsync;
  notifyRef.current = notify;

  useEffect(() => {
    if (data == null) {
      return;
    }
    const incoming = formStateFromApi(data);
    // why: a save echo is not a keystroke — writing it back after the draft moved drops or reorders letters.
    if (editGenerationRef.current !== appliedGenerationRef.current) {
      return;
    }
    if (
      editGenerationRef.current > 0 &&
      !sameWarholOutputForm(incoming, latestFormRef.current)
    ) {
      return;
    }
    latestFormRef.current = incoming;
    setForm(incoming);
  }, [data]);

  const persistForm = useCallback(
    async (payload: WarholOutputFormState, seq: number) => {
      setIsTyping(false);
      try {
        await mutateAsyncRef.current(payload);
        if (seq !== editGenerationRef.current) {
          return;
        }
        appliedGenerationRef.current = seq;
        setIsSaved(true);
        if (savedTimeoutRef.current != null) {
          clearTimeout(savedTimeoutRef.current);
        }
        savedTimeoutRef.current = setTimeout(
          () => setIsSaved(false),
          SAVED_ACK_MS,
        );
      } catch (error) {
        if (seq !== editGenerationRef.current) {
          return;
        }
        notifyRef.current({
          title: "Error saving output guidance",
          message: String(error),
          variant: "error",
        });
      }
    },
    [],
  );

  const scheduleSave = useCallback(
    (next: WarholOutputFormState, seq: number) => {
      setIsSaved(false);
      setIsTyping(true);
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        void persistForm(next, seq);
      }, DEBOUNCE_MS);
    },
    [persistForm],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        void persistForm(latestFormRef.current, editGenerationRef.current);
      }
      if (savedTimeoutRef.current != null) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, [persistForm]);

  const onTextChange = (key: keyof WarholOutputFormState, value: string) => {
    const next = { ...latestFormRef.current, [key]: value };
    editGenerationRef.current += 1;
    latestFormRef.current = next;
    setForm(next);
    scheduleSave(next, editGenerationRef.current);
  };

  return { form, isSaved, isTyping, onTextChange };
}

export function WarholOutputSettings() {
  const { t } = useTranslation("ui");
  const { data, isLoading } = useWarholOutputSettingsQuery();
  const { form, isSaved, isTyping, onTextChange } =
    useWarholOutputAutosave(data);

  return (
    <div
      className="h-full overflow-auto px-8 py-2"
      data-testid="warhol-output-settings-page"
    >
      <Flex direction="column" gap="4" className="px-2 py-4">
        <Flex
          align="center"
          gap="2"
          className="sticky top-0 z-[1] bg-puntt-neutral-gray-2 pb-2"
        >
          <Heading size="6" as="h1">
            {t("settings.warholOutput.title")}
          </Heading>
          {isTyping && (
            <Text
              size="2"
              color="gray"
              data-testid="warhol-output-settings-typing-indicator"
            >
              <Spinner size="1" className="animate-spin" />
            </Text>
          )}
          {isSaved && (
            <Text
              size="2"
              color="green"
              data-testid="guidelines-saved-indicator"
            >
              {t("settings.knowledgeSettingRoute.saved")}
            </Text>
          )}
        </Flex>
        <Text size="2" color="gray">
          {t("settings.warholOutput.description")}
        </Text>
        {isLoading ? (
          <Text size="2">{t("settings.warholOutput.loading")}</Text>
        ) : (
          WARHOL_OUTPUT_FIELDS.map((field) => {
            const label = t(field.labelKey);
            const id = `${field.testId}-field`;
            const value = form[field.key] as string;
            const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
              onTextChange(field.key, event.target.value);
            };
            return (
              <label key={field.testId} htmlFor={id}>
                <Text as="div" size="2" weight="medium" mb="1">
                  {label}
                </Text>
                <TextArea
                  id={id}
                  data-testid={field.testId}
                  aria-label={label}
                  value={value}
                  onChange={onChange}
                  rows={field.rows}
                  maxLength={field.maxLength}
                />
              </label>
            );
          })
        )}
      </Flex>
    </div>
  );
}
